import React, { useState, useEffect, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SearchIcon from '@mui/icons-material/Search';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import axiosInstance from "../../api/axiosInstance";
import moment from 'moment';
import "./home.scss";

const Home = () => {
    const today = new Date().toISOString().split('T')[0];
    const [searchList, setSearchList] = useState([]);
    const [search, setSearch] = useState("");
    const [categories, setCategories] = useState([]);
    const [selectedDate, setSelectedDate] = useState(today);
    const [searchModal, setSearchModal] = useState(false);
    const [filterModal, setFilterModal] = useState(false);

    const [viewMode, setViewMode] = useState(() => {
        const cached = localStorage.getItem("viewMode");
        return cached ? JSON.parse(cached) : 'week';
    });

    const [categoryGroup, setCategoryGroup] = useState(() => {
        return Number(localStorage.getItem("categoryGroup")) || 1;
    });

    const [defaultCategories, setDefaultCategories] = useState(() => {
        const cached = localStorage.getItem("defaultCategories");
        return cached ? JSON.parse(cached) : JSON.parse('{"1":[1],"2":[4]}');
    });

    const [activeCategories, setActiveCategories] = useState([]);

    useEffect(() => {
        const handleActiveCategories = () => {
            if (categories.length === 0) return;

            const groupCategories = categories
                .filter(c => c.layer === categoryGroup)
                .map(c => c.id);

            const defaults = defaultCategories[categoryGroup] || [];
            const filteredDefaults = defaults.filter(id => groupCategories.includes(id));

            setActiveCategories(filteredDefaults);
        };

        handleActiveCategories();
    }, [categoryGroup, defaultCategories, categories]);

    useEffect(() => {
        localStorage.setItem("categoryGroup", categoryGroup);
    }, [categoryGroup]);

    useEffect(() => {
        localStorage.setItem("viewMode", JSON.stringify(viewMode));
    }, [viewMode]);

    const isToday = selectedDate === today;

    const fetchData = useCallback(async () => {
        try {
            const [searchListRes, categoriesRes] = await Promise.all([
                axiosInstance.get(`searches/${search}`),
                axiosInstance.get(`categories`)
            ]);
            setSearchList(searchListRes.data.result);
            setCategories(categoriesRes.data);
        } catch (err) {
            console.error(err);
        }
    }, [search]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSetWeek = (startDate) => {
        const setDate = new Date(startDate).toISOString().split('T')[0];
        setSelectedDate(setDate);
        setSearchModal(false);
    };

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    const addDay = () => {
        const nextDate = new Date(selectedDate);
        nextDate.setDate(nextDate.getDate() + (viewMode === 'week' ? 7 : 1));
        setSelectedDate(nextDate.toISOString().split('T')[0]);
    };

    const subtractDay = () => {
        const prevDate = new Date(selectedDate);
        prevDate.setDate(prevDate.getDate() - (viewMode === 'week' ? 7 : 1));
        setSelectedDate(prevDate.toISOString().split('T')[0]);
    };

    const handleTodayClick = () => {
        setSelectedDate(today);
    };

    const handleCategorySelect = (categoryGroupValue) => {
        setCategoryGroup(categoryGroupValue);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value.toLowerCase());
    };

    const handleOpenSearch = useCallback(() => {
        setSearchModal(current => !current);
    }, []);

    const handleCloseSearch = useCallback(() => {
        setSearchModal(false);
    }, []);

    const handleOpenFilter = useCallback(() => {
        setFilterModal(current => !current);
    }, []);

    const toggleCategory = (id) => {
        setActiveCategories(prev =>
            prev.includes(id)
                ? prev.filter(catId => catId !== id)
                : [...prev, id]
        );
    };

    const toggleViewMode = () => {
        setViewMode(prevMode => (prevMode === "week" ? "day" : "week"));
    };

    const toggleDefaultCategory = (id) => {
        setDefaultCategories(prev => {
            const groupDefaults = prev[categoryGroup] || [];

            // Wenn ID bereits enthalten ist (soll entfernt werden)
            if (groupDefaults.includes(id)) {
                // Wenn es nur eine aktive Kategorie gibt, verhindere das Entfernen
                if (groupDefaults.length === 1) {
                    // Optional: Hinweis anzeigen, z. B. mit Toast o. Ä.
                    console.warn("Mindestens eine Standardkategorie muss ausgewählt bleiben.");
                    return prev;
                }

                const newGroupDefaults = groupDefaults.filter(catId => catId !== id);
                const updated = {
                    ...prev,
                    [categoryGroup]: newGroupDefaults
                };
                localStorage.setItem("defaultCategories", JSON.stringify(updated));
                return updated;
            } else {
                const newGroupDefaults = [...groupDefaults, id];
                const updated = {
                    ...prev,
                    [categoryGroup]: newGroupDefaults
                };
                localStorage.setItem("defaultCategories", JSON.stringify(updated));
                return updated;
            }
        });
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (
                event.key === 's' &&
                event.target.tagName !== 'INPUT' &&
                event.target.tagName !== 'TEXTAREA' &&
                event.target.contentEditable !== 'true'
            ) {
                event.preventDefault();
                handleOpenSearch();
            }

            if (event.key === 'Escape') {
                handleCloseSearch();
                setFilterModal(false)
            }

            if (
                event.key === 'd' &&
                event.target.tagName !== 'INPUT' &&
                event.target.tagName !== 'TEXTAREA' &&
                event.target.contentEditable !== 'true'
            ) {
                event.preventDefault();
                toggleViewMode();
            }

            if (
                event.key === 'f' &&
                event.target.tagName !== 'INPUT' &&
                event.target.tagName !== 'TEXTAREA' &&
                event.target.contentEditable !== 'true'
            ) {
                event.preventDefault();
                handleOpenFilter();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleOpenSearch, handleCloseSearch, handleOpenFilter]);

    return (
        <div className="home">
            <div className="top">
                <div className="left">
                    <button className="light search" title='Filter mit "F" öffnen' onClick={handleOpenFilter}>
                        <FilterAltOutlinedIcon />
                    </button>
                    <button className="light search" title='Ansicht mit "D" wechseln' onClick={toggleViewMode}>
                        {viewMode === 'week'
                            ? <DateRangeOutlinedIcon />
                            : <EventOutlinedIcon />
                        }
                    </button>
                    <button className="light search" title='Suche mit "S" öffnen' onClick={handleOpenSearch}>
                        <SearchIcon />
                    </button>
                </div>
                <div className="middle">
                    {viewMode === 'week'
                        ? <span>KW {moment(selectedDate).format("W - MMMM")}</span>
                        : <span>{moment(selectedDate).format("dddd, D. MMMM")}</span>
                    }

                </div>
                <div className="right">
                    <input type="date" value={selectedDate} onChange={handleDateChange} />
                    <button onClick={subtractDay} className="light arrow"><KeyboardArrowLeftIcon /></button>
                    <button onClick={handleTodayClick} className={isToday ? "light" : ""}>Heute</button>
                    <button onClick={addDay} className="light arrow"><KeyboardArrowRightIcon /></button>
                </div>
            </div>

            <div className="content">
                <Outlet context={{
                    category: activeCategories,
                    selectedDate,
                    viewMode,
                    setViewMode,
                    setSelectedDate
                }} />
            </div>

            {searchModal && (
                <motion.div className="searchModal" onClick={handleOpenSearch} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1, ease: "easeOut" }}>
                    <motion.div className="searchBar" onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.1, ease: "easeOut" }}>
                        <SearchIcon />
                        <input type="search" placeholder="Suche nach Aufträgen" value={search} onChange={handleSearchChange} autoFocus />
                        <ul className="searchList">
                            {searchList.map((search, index) => (
                                <li className="item" key={index} onClick={() => handleSetWeek(search.startDate)}>
                                    <div className="dot" style={{ background: search.statusColor }} title={search.statusName} />
                                    <div className="content">
                                        <span className="name">{search.description} {search.customer}</span>
                                        <span className="date">
                                            {moment(search.startDate).format("DD.MM.") + (search.ganztags ? "" : " von " + moment(search.startTime, "HH:mm:ss").format("H:mm") + " Uhr")}
                                            {" - "}
                                            {moment(search.endDate).format("DD.MM.") + (search.ganztags ? "" : " bis " + moment(search.endTime, "HH:mm:ss").format("H:mm") + " Uhr")}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </motion.div>
            )}

            {filterModal && (
                <motion.div className="filterModal" onClick={handleOpenFilter} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1, ease: "easeOut" }}>
                    <motion.div className="filterContent" onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.1, ease: "easeOut" }}>
                        <h4>Filter</h4>
                        <div className="category">
                            <button className={categoryGroup === 1 ? "solid active" : "solid"} onClick={() => handleCategorySelect(1)}>
                                <span>Fuhrpark</span>
                            </button>
                            <button className={categoryGroup === 2 ? "solid active" : "solid"} onClick={() => handleCategorySelect(2)}>
                                <span>Team</span>
                            </button>
                        </div>
                        <div className="filterRow">
                            <h5>Kategorie wählen</h5>
                            <div className="list">
                                {categories
                                    .filter(c => c.layer === categoryGroup)
                                    .map((category) => (
                                        <button
                                            key={category.id}
                                            className={`light ${activeCategories.includes(category.id) ? "active" : ""}`}
                                            onClick={() => toggleCategory(category.id)}
                                            aria-pressed={activeCategories.includes(category.id)}
                                        >
                                            {category.name}
                                            <div
                                                className="starIcon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleDefaultCategory(category.id);
                                                }}
                                                title="Als Standardkategorie speichern/entfernen"
                                            >
                                                {defaultCategories[categoryGroup]?.includes(category.id) ? (
                                                    <StarIcon />
                                                ) : (
                                                    <StarOutlineIcon />
                                                )}
                                            </div>
                                        </button>
                                    ))}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};

export default Home;